import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { ObjectType } from '@/core/models/document.model'
import { useGetPatientFilesQuery } from '@/core/services/patient'

import { useAppSelector } from '@/redux/rootReducer'
import {
  addDocument,
  deleteDocuments,
  selectDocumentCreateRequestTracker,
  selectDocumentDeleteRequestTracker,
  selectDocumentUpdateRequestTracker,
  updateFilename,
} from '@/redux/reducers/document.reducer'
import { selectMagicLink } from '@/redux/reducers/auth.reducer'

import type { PatientDocumentListProps } from '../ui/PatientDocumentList'

const DEFAULT_PAGE_SIZE = 12

export function usePatientDocuments(patientId?: string) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState<number>(1)
  const pageSize = DEFAULT_PAGE_SIZE

  const { data: patientFiles, refetch, isFetching } = useGetPatientFilesQuery(
    patientId
      ? {
          patientId,
          limit: pageSize,
          skip: (page - 1) * pageSize,
        }
      : skipToken
  )

  const addDocumentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
      refetch()
    },
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const updateDocumentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.updated.success'), { variant: 'success' })
      refetch()
    },
    onError: () => enqueueSnackbar(t('notification.document.updated.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentUpdateRequestTracker,
  })

  const deleteDocumentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.delete.success'), { variant: 'success' })
      refetch()
    },
    onError: () => enqueueSnackbar(t('notification.document.delete.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentDeleteRequestTracker,
  })

  useEffect(() => {
    setPage(1)
  }, [patientId])

  const documents = useMemo(
    () =>
      patientFiles
        ? _.map(patientFiles.items, (file) => ({
            filename: file.filename,
            key: file.key,
            url: file.url,
          }))
        : [],
    [patientFiles]
  )

  const qrCode = useAppSelector(selectMagicLink)

  const handleDelete = (deletedDocuments: PatientDocumentListProps['documents']) => {
    if (!patientId || !deletedDocuments.length) return
    const docs = _.map(deletedDocuments, (document) => ({
      filename: document.filename,
      key: document.key,
      objectType: 'patient' as ObjectType,
    }))
    deleteDocumentRequestHandler.dispatch(deleteDocuments(docs, 'patient', patientId))
  }

  const handleSaveFilename = (newFilename: string, key: string) => {
    const file = _.find(patientFiles?.items, (item) => item.key === key)
    if (!file || !patientId) return
    updateDocumentRequestHandler.dispatch(updateFilename(_.omit(file, 'url'), newFilename, 'patient', patientId))
  }

  const handleSaveFileAnnotation = (file: File, key: string) => {
    if (patientId) addDocumentRequestHandler.dispatch(addDocument({ documentFile: file, objectType: 'patient', objectId: patientId }))
  }

  const handleUpload = (documentFile: File) => {
    if (patientId) addDocumentRequestHandler.dispatch(addDocument({ documentFile, objectType: 'patient', objectId: patientId }))
  }

  const handleRefresh = () => {
    refetch()
  }

  return {
    documents,
    totalCount: patientFiles?.totalCount,
    isDeleting: deleteDocumentRequestHandler.requestTracker.loading,
    isFetching,
    page,
    pageSize,
    qrCode,
    onDelete: handleDelete,
    onSaveFileName: handleSaveFilename,
    onSaveFileAnnotation: handleSaveFileAnnotation,
    onUpload: handleUpload,
    onPageChange: setPage,
    onRefresh: handleRefresh,
  }
}
